import { SIDEBAR_GRIDLINE_OVERLAY } from "@/components/layout/sidebarLayout.const";

/** Compact vertical padding for navy profile section headers (Business Overview, etc.) */
export const PROFILE_SECTION_HEADER_PY = { base: 6, sm: 8 } as const;

/** Taller pipeline sidebar headers (ecosystem corridor membership steps). */
export const PROFILE_PIPELINE_HEADER_PY = { base: 8, sm: 10 } as const;

export const PROFILE_SECTION_HEADER_PX = { base: "md", sm: "xl" } as const;

export const PROFILE_SECTION_HEADER_ICON_SIZE = 18;

export const PROFILE_PIPELINE_HEADER_ICON_SIZE = 20;

export const PROFILE_SECTION_HEADER_TITLE_FZ = "sm" as const;

export const PROFILE_PIPELINE_HEADER_TITLE_FZ = "md" as const;

/** Vertical space between stacked profile section cards */
export const PROFILE_SECTION_GAP = 10;

/** Matches `radius="lg"` on profile section cards (Business Overview, etc.). */
export const PROFILE_SECTION_RADIUS = "var(--mantine-radius-lg)";

/** Grey border + layered shadow for raised profile cards (header, accordions, images). */
export const PROFILE_CARD_BORDER_COLOR = "#D1D5DB";

export const PROFILE_CARD_BORDER = `1px solid ${PROFILE_CARD_BORDER_COLOR}`;

export const PROFILE_CARD_3D_SHADOW =
  "0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 12px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)";

export const PROFILE_CARD_PAPER_STYLE = {
  backgroundColor: "#ffffff",
  border: PROFILE_CARD_BORDER,
  boxShadow: PROFILE_CARD_3D_SHADOW,
  overflow: "hidden" as const,
};

export const PROFILE_HEADER_CARD_PAPER_STYLE = {
  ...PROFILE_CARD_PAPER_STYLE,
  maxWidth: "100%",
} as const;

/** Brand accents shared by profile headers and “What We Offer”. */
export const PROFILE_ACCENT_NAVY = "#04305c";

export const PROFILE_ACCENT_GOLD = "#ffc300";

/** Company header (anonymised through match) — same navy + grid as the company sidebar. */
export const PROFILE_HEADER_NAVY_BORDER = "rgba(255, 255, 255, 0.14)";

export const PROFILE_HEADER_ON_NAVY_TITLE = "#FFFFFF";

export const PROFILE_HEADER_ON_NAVY_MUTED = "#CBD5E1";

export const PROFILE_HEADER_ON_NAVY_LINK = "#93C5FD";

export const PROFILE_HEADER_ON_NAVY_DIVIDER = "1px solid rgba(226, 232, 240, 0.28)";

export const PROFILE_HEADER_CARD_NAVY_PAPER_STYLE = {
  backgroundColor: "var(--mantine-color-primary-6)",
  backgroundImage: SIDEBAR_GRIDLINE_OVERLAY,
  backgroundSize: "auto",
  backgroundPosition: "top left",
  border: `1px solid ${PROFILE_HEADER_NAVY_BORDER}`,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.12), 0 8px 24px rgba(4, 48, 92, 0.28)",
  overflow: "hidden" as const,
  maxWidth: "100%",
} as const;

export const PROFILE_HEADER_ON_NAVY_BOOKMARK_BTN_STYLES = {
  root: {
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    color: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.45)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.22)",
      borderColor: "rgba(226, 232, 240, 0.7)",
    },
  },
} as const;

/** Primary request/join CTA on navy profile headers (matches Explore Opportunities / card request buttons). */
export const PROFILE_HEADER_ON_NAVY_CTA_BTN_STYLES = {
  root: {
    backgroundColor: PROFILE_ACCENT_GOLD,
    color: PROFILE_ACCENT_NAVY,
    border: `1px solid ${PROFILE_ACCENT_GOLD}`,
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#e6b000",
      borderColor: "#e6b000",
    },
  },
} as const;

/** Destructive / withdraw CTA on navy profile headers. */
export const PROFILE_HEADER_ON_NAVY_CANCEL_BTN_STYLES = {
  root: {
    backgroundColor: "#ffffff",
    color: "#c92a2a",
    border: "1px solid #ffffff",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#f8fafc",
      borderColor: "#f8fafc",
    },
  },
  section: {
    color: "#c92a2a",
  },
} as const;

/** Long-form profile copy (descriptions, labels) — wraps on narrow viewports without clipping. */
export const PROFILE_FIELD_TEXT_STYLE = {
  lineHeight: 1.5,
  wordBreak: "break-word" as const,
  overflowWrap: "anywhere" as const,
  whiteSpace: "normal" as const,
};

/** Company description and other multi-paragraph profile copy. */
export const PROFILE_PARAGRAPH_TEXT_STYLE = {
  ...PROFILE_FIELD_TEXT_STYLE,
  whiteSpace: "pre-wrap" as const,
};

/** Sub-section labels (PRODUCTS, COMPANY DESCRIPTION, CORE CAPABILITIES, etc.) */
export const PROFILE_SUBSECTION_LABEL_PROPS = {
  size: "md" as const,
  c: "slate.8",
  tt: "uppercase" as const,
  mb: "sm" as const,
  fw: 600,
};

/** Item titles within a sub-section (product/service name) */
export const PROFILE_ITEM_TITLE_PROPS = {
  size: "sm" as const,
  c: "slate.8",
  fw: 700,
  mb: "xs" as const,
};

/** Empty / N/A field values */
export const PROFILE_EMPTY_VALUE_PROPS = {
  size: "sm" as const,
  c: "slate.6",
};

/** Bold, high-contrast labels for profile edit forms (Mantine Input `labelProps`). */
export const PROFILE_FORM_LABEL_PROPS = {
  fw: 700,
  fz: "sm",
  c: "#0F172A",
  tt: "capitalize" as const,
} as const;

/** Section headings above grouped fields (e.g. Focus sectors, What you get). */
export const PROFILE_FORM_SECTION_LABEL_PROPS = {
  size: "sm" as const,
  fw: 700,
  c: "#0F172A",
} as const;
